import React from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { cn } from '../../../lib/cn';
import { Icon } from '../../Icon';
import { useFieldControl } from './Field';

/**
 * Select, the custom dropdown.
 *
 * Source selectors: `system.css` `.dsk-select`, `__button`, `__caret`,
 * `__list`, `__option`, `__native`.
 *
 * This is the React port of `upgradeSelect` in
 * `design-system/behaviours/desktop-behaviours.reference.js`. That script wraps
 * a real `<select>`, builds a button plus a listbox from its options, and keeps
 * the native control in the DOM so labels, form submission and change events
 * keep working. Same contract here, with the imperative DOM work replaced by
 * state: the native `<select>` is still rendered, still carries the `name` and
 * the value, and still fires `change` for anything listening to the form.
 *
 * Why a custom shell at all: a native `<select>` paints its own arrow and its
 * own popup, and neither honours our radius, our type ramp or the dark theme.
 *
 * Doctrine enforced:
 * - A CHOSEN option is state, not a command, so `.is-selected` is a tinted
 *   accent row, never an ink fill and never a tick glyph that can drift out of
 *   alignment. That is the CSS's decision; this component only sets the class.
 * - `:focus-visible` rings come from the CSS on both the button and the option.
 *
 * Keyboard, the full set the reference implements plus the two it left out:
 *   Enter / Space   open (native button activation), select when on an option
 *   ArrowDown / Up  open from the button, move between options when open
 *   Home / End      first / last option
 *   Escape          close and return focus to the button
 *   Tab             close, then let focus leave normally
 * `aria-expanded` lives on the button, `role="listbox"` on the list and
 * `role="option"` with `aria-selected` on each row.
 *
 * Two API notes. `...rest` lands on the `.dsk-select` root, as it does in every
 * other component here, but `ref` deliberately points at the hidden native
 * `<select>`: that is the element a form library wants to register, and the
 * root div has nothing to offer it.
 *
 *   <Select
 *     options={[{ value: 'nm', label: 'Near Mint' }, { value: 'lp', label: 'Lightly Played' }]}
 *     value={condition}
 *     onChange={setCondition}
 *   />
 */
function normaliseOptions(options) {
  return options.map((opt) =>
    typeof opt === 'object' && opt !== null
      ? { value: String(opt.value), label: opt.label ?? String(opt.value) }
      : { value: String(opt), label: String(opt) },
  );
}

export const Select = React.forwardRef(function Select(
  {
    options = [],
    value,
    defaultValue,
    onChange,
    placeholder = 'Select',
    name,
    disabled = false,
    required,
    label,
    id,
    className,
    ...rest
  },
  ref,
) {
  const items = React.useMemo(() => normaliseOptions(options), [options]);
  const field = useFieldControl({ id });

  const uid = React.useId();
  const buttonId = `${uid}-button`;
  const listId = `${uid}-list`;

  const isControlled = value !== undefined;
  const [internal, setInternal] = React.useState(
    defaultValue !== undefined ? String(defaultValue) : items.length ? items[0].value : '',
  );
  const current = isControlled ? (value === null ? '' : String(value)) : internal;

  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  const buttonRef = React.useRef(null);
  const optionRefs = React.useRef([]);

  const selectedIndex = items.findIndex((o) => o.value === current);
  const selectedLabel = selectedIndex >= 0 ? items[selectedIndex].label : placeholder;

  const close = React.useCallback((refocus) => {
    setOpen(false);
    if (refocus && buttonRef.current) buttonRef.current.focus();
  }, []);

  const commit = React.useCallback(
    (index) => {
      const item = items[index];
      if (!item) return;
      if (!isControlled) setInternal(item.value);
      if (onChange) onChange(item.value, item);
      close(true);
    },
    [items, isControlled, onChange, close],
  );

  // Pointer outside the wrapper closes the list. Bound on the document while
  // open only, so a page full of selects costs one listener, not N.
  React.useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event) {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown, true);
    return () => document.removeEventListener('mousedown', onPointerDown, true);
  }, [open]);

  // Opening moves focus onto the chosen row, so the list starts where the value
  // already is rather than at the top of an unrelated scroll position.
  React.useEffect(() => {
    if (!open) return;
    const target = optionRefs.current[selectedIndex >= 0 ? selectedIndex : 0];
    if (target) target.focus();
  }, [open, selectedIndex]);

  function focusOption(index) {
    const count = items.length;
    if (!count) return;
    const next = ((index % count) + count) % count;
    const target = optionRefs.current[next];
    if (target) target.focus();
  }

  function currentFocusIndex() {
    return optionRefs.current.indexOf(document.activeElement);
  }

  function onButtonKeyDown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
    }
  }

  function onListKeyDown(event) {
    const at = currentFocusIndex();
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        close(true);
        break;
      case 'ArrowDown':
        event.preventDefault();
        focusOption(at + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusOption(at - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusOption(0);
        break;
      case 'End':
        event.preventDefault();
        focusOption(items.length - 1);
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  }

  // The real control stays in the DOM. It carries the name and the value, so a
  // surrounding <form> reads this exactly as it would a plain <select>.
  function onNativeChange(event) {
    const next = event.target.value;
    if (!isControlled) setInternal(next);
    const index = items.findIndex((o) => o.value === next);
    if (onChange) onChange(next, items[index]);
  }

  const labelledBy = [field.labelId, buttonId].filter(Boolean).join(' ') || undefined;

  return (
    <div ref={wrapRef} className={cn('dsk-select', className)} {...rest}>
      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        className="dsk-select__button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-labelledby={field.labelId ? labelledBy : undefined}
        aria-label={!field.labelId && label ? label : undefined}
        aria-describedby={field['aria-describedby']}
        disabled={disabled}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        onKeyDown={onButtonKeyDown}
      >
        <span>{selectedLabel}</span>
        <Icon as={CaretDown} className="dsk-select__caret" />
      </button>

      {open ? (
        <div id={listId} className="dsk-select__list" role="listbox" onKeyDown={onListKeyDown}>
          {items.map((item, index) => (
            <button
              key={item.value}
              ref={(node) => {
                optionRefs.current[index] = node;
              }}
              type="button"
              role="option"
              aria-selected={index === selectedIndex}
              className={cn('dsk-select__option', index === selectedIndex && 'is-selected')}
              onClick={() => commit(index)}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}

      <select
        ref={ref}
        className="dsk-select__native"
        tabIndex={-1}
        aria-hidden="true"
        name={name}
        required={required}
        disabled={disabled}
        value={current}
        onChange={onNativeChange}
        id={field.id}
      >
        {selectedIndex < 0 ? <option value="">{placeholder}</option> : null}
        {items.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </select>
    </div>
  );
});
Select.displayName = 'Select';

export default Select;
